import { mergeAttributes, Node, Commands } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { toLightweightDeviation } from '../utils/toLightweightDeviation';
import Component from './components/GalleryNodeView';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  deleteExtension,
  getExternalRenderer,
} from '../../components/utils/tiptap';
import { EXTENSION_TYPE_NAME } from './constants';
import omitBy from 'lodash/omitBy';

export interface DaGalleryOptions {
  defaultWidth: number;
  formMaxWidth?: number | null;
  defaultAlignment?: DaGalleryAttrs['alignment'];
  maxWidth?: number;
  minWidth?: number;
  defaultGridItemWidth?: number;
  defaultGridItemHeight?: number;
  defaultTorpedoRowHeight?: number;
  defaultLayout?: DaGalleryAttrs['layout'];
  defaultLayoutSpacing?: number;
  smallMedia?: boolean;
  canAlign?: boolean;
  canLink?: boolean;
  canConfigure?: boolean;
  canResize?: boolean;
}

export interface DaGalleryAttrs {
  width: number | null;
  alignment: 'left' | 'right' | 'center';
  deviations: PapiDeviation[];
  layout: 'torpedo' | 'grid';
  layoutSpacing: number | null;
  layoutRowAlignment: 'left' | 'center' | 'right';
  itemCropping: 'crop' | 'fit';
  gridItemWidth: number | null;
  gridItemHeight: number | null;
  torpedoRowHeight: number | null;
  linkOverrides: Record<number, string>;
}

export enum MenuState {
  HIDDEN,
  SHOW,
}

export type DaGalleryStorage = {
  menuState: MenuState;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    gallery: {
      daGalleryAddDeviations: (
        deviation: PapiDeviation[],
        isEdit: boolean
      ) => ReturnType;
      daGalleryOpenSettingsMenu: () => ReturnType;
      daGalleryCloseSettingsMenu: () => ReturnType;
      daGalleryDelete: () => ReturnType;
      daGalleryAlign: (alignment: 'left' | 'right' | 'center') => ReturnType;
      daGalleryChangeLayout: (layout: DaGalleryAttrs['layout']) => ReturnType;
      daGallerySetWidth: (width: number) => ReturnType;
      daGallerySetGridItemWidth: (width: number) => ReturnType;
      daGallerySetGridItemHeight: (height: number) => ReturnType;
      daGallerySetLayoutSpacing: (value: number) => ReturnType;
      daGallerySetTorpedoRowHeight: (value: number) => ReturnType;
      daGallerySetItemCropping: (value: number) => ReturnType;
      daGallerySetRowAlignment: (value: number) => ReturnType;
      daGallerySetLinkOverrides: (value: Record<number, string>) => ReturnType;
    };
  }
}

export const DaGallery = Node.create<DaGalleryOptions, DaGalleryStorage>({
  name: 'da-gallery',
  group: 'block unlinkable',
  inline: false,
  draggable: true,

  addStorage() {
    return {
      menuState: MenuState.HIDDEN,
    };
  },

  addOptions() {
    return {
      // HTMLAttributes: {},
      defaultWidth: 780,
      formMaxWidth: 780,
      smallMedia: false,
      canLink: true,
      canConfigure: true,
      canResize: true,
    };
  },

  addAttributes() {
    return {
      deviations: {
        default: null,
      },
      width: {
        default: null,
      },
      alignment: {
        default: this.options.defaultAlignment ?? 'center',
      },
      layout: {
        default: 'grid',
      },
      layoutSpacing: {
        default: 8,
      },
      layoutRowAlignment: {
        default: 'left',
      },
      itemCropping: {
        default: 'crop',
      },
      gridItemWidth: {
        default: null,
      },
      gridItemHeight: {
        default: null,
      },
      torpedoRowHeight: {
        default: null,
      },
      linkOverrides: {
        default: {},
      },
    };
  },

  /**
   * Used to parse incoming html, usually via paste
   */
  parseHTML() {
    return [
      {
        tag: `div[data-da-type="${EXTENSION_TYPE_NAME}"]`,
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }

          const dataType = node.getAttribute('data-da-type');
          if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
            return false;
          }
          try {
            let deviations: PapiDeviation[] = [];
            let linkOverrides = {};

            if (node.getAttribute('data-deviations') !== null) {
              deviations = JSON.parse(
                node.getAttribute('data-deviations') ?? '[]'
              );
              linkOverrides = JSON.parse(
                node.getAttribute('data-link-overrides') ?? '{}'
              );
            } else {
              const innerDeviations = node.querySelectorAll(
                'div[data-deviation]'
              );
              innerDeviations.forEach(iNode => {
                deviations.push(
                  JSON.parse(`${iNode.getAttribute('data-deviation')}`)
                );
              });

              const links = node.querySelectorAll('a[data-deviationId]');
              links.forEach(link => {
                linkOverrides[link.getAttribute('data-deviationId') ?? ''] =
                  link.getAttribute('href');
              });
            }

            const attrs = {
              width: maybeNumber(node.getAttribute('data-width') ?? undefined),
              deviations,
              linkOverrides,
              layout: node.getAttribute('data-layout') ?? undefined,
              layoutSpacing: maybeNumber(
                node.getAttribute('data-layoutspacing') ?? undefined
              ),
              layoutRowAlignment:
                node.getAttribute('data-layoutrowalignment') ?? undefined,
              itemCropping: node.getAttribute('data-itemcropping') ?? undefined,
              gridItemWidth: maybeNumber(
                node.getAttribute('data-griditemwidth') ?? undefined
              ),
              gridItemHeight: maybeNumber(
                node.getAttribute('data-griditemheight') ?? undefined
              ),
              torpedoRowHeight: maybeNumber(
                node.getAttribute('data-torpedorowheight') ?? undefined
              ),
            };
            return attrs;
          } catch (e) {}
          return null;
        },
      },
    ];
  },

  /**
   * Used to render HTML version of the doc, this is not used for viewer and not used
   * in edit mode either.
   *
   * Will be used for public API on the server
   *
   * Output somehting like:
   *
   * <div class="gallery" data-type="grid">
   *   <img />
   *   <img />
   *   ...
   * </div>
   *
   */
  renderHTML({ HTMLAttributes }) {
    try {
      const { deviations, linkOverrides } = HTMLAttributes as DaGalleryAttrs;
      const deviationMediaService = getExternalRenderer(
        this.editor!,
        'mediaService'
      );

      if (!deviationMediaService) {
        return ['div'];
      }

      if (deviations.length === 0) {
        return ['span'];
      }

      const imgs = deviations.map(deviation => {
        const props: any = {};
        try {
          const dev = deviationMediaService.getSrcSetForDeviationPreview(
            deviation,
            true,
            780
          );
          props.src = dev.src;
          props.srcSet = dev.srcSet;
        } catch (e) {
          const dev = deviationMediaService.getSmallestThumbForArtStage(
            deviation,
            250,
            250
          );
          props.src = dev?.src;
        }

        return [
          'a',
          {
            href: linkOverrides?.[deviation.deviationId]
              ? linkOverrides[deviation.deviationId]
              : deviation.url,
          },
          [
            'img',
            mergeAttributes({
              alt: deviation.title,
              ...props,
              'data-deviation-item': JSON.stringify(deviation),
            }),
          ],
        ];
      });

      const attribs = {};
      for (const key of Object.keys(HTMLAttributes)) {
        if (key === 'deviations' || key === 'linkOverrides') {
          continue;
        }
        attribs[`data-${key}`] = HTMLAttributes[key];
      }

      return [
        'div',
        mergeAttributes({
          'data-da-type': 'da-gallery',
          'data-deviations': JSON.stringify(deviations),
          'data-linkoverrides': JSON.stringify(linkOverrides),
          ...attribs,
        }),
        ...imgs,
      ];
    } catch (e) {
      return ['span'];
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component as any);
  },

  onSelectionUpdate() {
    if (this.editor.isActive('da-gallery')) {
      this.editor.commands.daGalleryOpenSettingsMenu();
    } else {
      this.editor.commands.daGalleryCloseSettingsMenu();
    }
  },

  addCommands(): Commands['gallery'] {
    return {
      daGalleryAddDeviations:
        (deviations, isEdit) =>
        ({ commands, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
            if (isEdit) {
              return commands.updateAttributes(this.name, {
                deviations,
              });
            }
            return commands.insertContent({
              type: this.name,
              attrs: {
                deviations: deviations.map(toLightweightDeviation),
              },
            });
          }
          return true;
        },
      daGalleryOpenSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
          }
          return true;
        },
      daGalleryCloseSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.HIDDEN;
          }
          return true;
        },
      daGalleryDelete:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (deleteExtension(EXTENSION_TYPE_NAME, editor, dispatch, state)) {
            this.storage.menuState = MenuState.HIDDEN;
            return true;
          }
          return true;
        },
      daGalleryAlign:
        alignment =>
        ({ editor, chain, dispatch, commands }) => {
          if (!this.options.canAlign) {
            return false;
          }
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              alignment,
            });
          }
          return true;
        },
      daGallerySetWidth:
        width =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              width: +width,
            });
          }
          return true;
        },
      daGallerySetGridItemWidth:
        width =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              gridItemWidth: +width,
            });
          }
          return true;
        },
      daGallerySetGridItemHeight:
        height =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              gridItemHeight: +height,
            });
          }
          return true;
        },
      daGallerySetLayoutSpacing:
        value =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              layoutSpacing: +value,
            });
          }
          return true;
        },
      daGallerySetTorpedoRowHeight:
        value =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              torpedoRowHeight: +value,
            });
          }
          return true;
        },
      daGallerySetItemCropping:
        value =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              itemCropping: value,
            });
          }
          return true;
        },
      daGallerySetRowAlignment:
        value =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              layoutRowAlignment: value,
            });
          }
          return true;
        },
      daGallerySetLinkOverrides:
        value =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              linkOverrides: omitBy(value, v => {
                return v === '' || !v;
              }),
            });
          }
          return true;
        },
      daGalleryChangeLayout:
        layout =>
        ({ editor, chain, dispatch, commands }) => {
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              layout,
            });
          }
          return true;
        },
    };
  },
});

function maybeNumber(value: string | number | undefined): number | undefined {
  if (value === undefined) {
    return value;
  }
  return +value;
}
